/**
 * ============================================
 * DELIVERY GEOLOCATION - COMPONENTE MODULAR
 * ============================================
 * Maneja la selección de ubicación para delivery usando Google Maps
 * 🗺️ Geolocalización con marcador arrastrable
 * ✅ Validación de área de cobertura
 */

class DeliveryGeolocation {
  constructor(options) {
    this.containerId = options.containerId;
    this.mapElementId = options.mapElementId || 'delivery-map';
    this.attributeName = options.attributeName || 'delivery_location';
    this.onLocationSelect = options.onLocationSelect || null;
    this.storageKey = `cart_delivery_location`;
    
    // Coordenadas por defecto (Asunción, Paraguay)
    this.defaultLat = options.defaultLat || -25.2637;
    this.defaultLng = options.defaultLng || -57.5759;
    
    // Estado
    this.map = null;
    this.marker = null;
    this.currentLocation = this.loadLocation();
    this.geocoder = null;
    this.addressInfoElement = null;
    this.confirmButton = null;
    this.coveragePolygon = null;
    this.coverageArea = null;
    this.warningElement = null;
    
    // Definir área de cobertura (coordenadas del polígono)
    this.coverageCoordinates = [
      [-25.097429664222,-57.503660070872],[-25.102306950462,-57.517414438701],
      [-25.114023301345,-57.527048933482],[-25.145533351391,-57.536018240428],
      [-25.171908507758,-57.537606108165],[-25.19361808855,-57.555330145335],
      [-25.244789222479,-57.59901796577],[-25.256860606694,-57.629187452769],
      [-25.278244582573,-57.663476812816],[-25.290506650873,-57.670171606517],
      [-25.313475289545,-57.670686590648],[-25.331086802424,-57.662704336619],
      [-25.343925299368,-57.64772688148],[-25.356141939116,-57.64579569099],
      [-25.360175098373,-57.647158253169],[-25.36420812307,-57.644400942302],
      [-25.368241013192,-57.63648306129],[-25.382510158957,-57.620303976512],
      [-25.417400421653,-57.575757849193],[-25.436275722545,-57.557132589793],
      [-25.457472996552,-57.547777044749],[-25.470162448329,-57.552197325206],
      [-25.474492054231,-57.558441507793],[-25.479751298703,-57.562625753856],
      [-25.499643302511,-57.569363462901],[-25.503298850544,-57.574094879603],
      [-25.508503577943,-57.576423037028],[-25.519841840125,-57.569921362376],
      [-25.52646420656,-57.559793341136],[-25.518931720269,-57.54151140449],
      [-25.510624137077,-57.523401129222],[-25.503148757385,-57.498510229564],
      [-25.500940913754,-57.475335943675],[-25.508842482602,-57.42718492744],
      [-25.521236052904,-57.391393530345],[-25.523811431135,-57.36997877357],
      [-25.521429692286,-57.350623953319],[-25.533705791215,-57.330110418773],
      [-25.564448612043,-57.302472937083],[-25.651291188862,-57.147634375071],
      [-25.640303905058,-57.121198523021],[-25.598047606764,-57.143085348582],
      [-25.577146115002,-57.179563391185],[-25.548497402109,-57.204282629466],
      [-25.51697590744,-57.232435095286],[-25.45383058613,-57.273977148509],
      [-25.424532299628,-57.276981222606],[-25.397940652663,-57.270715582347],
      [-25.355521441135,-57.236211645579],[-25.240985129412,-57.327535498118],
      [-25.217070961243,-57.363412725902],[-25.205422748203,-57.368991720653],
      [-25.196258703888,-57.38830362556],[-25.157109600827,-57.415769445872],
      [-25.14125996171,-57.427099096751],[-25.112507816693,-57.463319647288],
      [-25.097429664222,-57.503660070872]
    ];
  }

/**
 * Carga la ubicación guardada aplicando una jerarquía de 3 niveles.
 *
 * El criterio de prioridad es si el cliente confirmó una ubicación
 * en esta sesión activa, no el tiempo transcurrido.
 * La sesión se considera activa mientras el localStorage tenga
 * el flag `sessionActive: true`, que se setea al confirmar y se
 * borra cuando ocurre cualquiera de estos eventos (ver main-cart-items.liquid):
 *   • El carrito queda con 0 items (compra completada o vaciado manual)
 *   • El cliente hace logout (click en link de /account/logout)
 *
 * ─────────────────────────────────────────────────────────────────────
 * NIVEL 1 — localStorage con sesión activa (confirmó en esta sesión)
 *   El cliente ya eligió una ubicación y aún no terminó ni canceló.
 *   Ejemplo: confirmó en el mapa → fue al checkout → volvió atrás.
 *   → Debe ver SU ubicación confirmada, no la de una orden vieja.
 *   → La sesión muere cuando el carrito se vacía o hace logout.
 *
 * NIVEL 2 — Última orden del cliente (cross-device, inyectado por Liquid)
 *   El cliente está logueado pero no tiene sesión activa en este device.
 *   Shopify inyecta window.__savedDeliveryLocation desde la última orden
 *   con delivery (ver main-cart-items.liquid línea ~72).
 *   Ejemplo: compró desde la PC, ahora abre el carrito en el celular.
 *   → Mapa pre-cargado en su ubicación habitual.
 *   → Al cargar desde servidor, NO se marca como sesión activa todavía.
 *     Recién se activa cuando el cliente confirma en el mapa.
 *
 * NIVEL 3 — localStorage sin sesión activa (fallback final)
 *   No hay sesión activa NI datos del servidor.
 *   Puede ser: cliente sin cuenta, o logueado cuya última orden fue pickup.
 *   Usamos lo que haya en localStorage como referencia geográfica,
 *   aunque no esté marcado como sesión activa.
 *   Ejemplo: cliente sin cuenta, mismo device, sesión anterior.
 *   → Al menos el mapa abre cerca de donde suele pedir.
 *
 * Si ningún nivel tiene datos: retorna null → mapa abre en Asunción.
 * ─────────────────────────────────────────────────────────────────────
 */
loadLocation() {
  // ── Leer y parsear localStorage una sola vez ──────────────────────
  const stored = localStorage.getItem(this.storageKey);
  let localLocation = null;

  if (stored) {
    try {
      localLocation = JSON.parse(stored);
    } catch (e) {
      // Valor corrupto, lo descartamos silenciosamente
      console.error('❌ Error al parsear ubicación en localStorage:', e);
    }
  }

  // ── NIVEL 1: sesión activa confirmada por el cliente ─────────────
  // `sessionActive: true` es seteado por saveLocation() cuando el cliente
  // confirma su ubicación en el mapa. Se borra desde main-cart-items.liquid
  // cuando el carrito queda vacío o el cliente hace logout.
  // Mientras esté activa, esta ubicación tiene prioridad absoluta.
  if (localLocation?.valid && localLocation?.sessionActive === true) {
    console.log('📍 NIVEL 1 — Sesión activa: ubicación confirmada en esta sesión:', localLocation);
    console.log(`   Coordenadas: ${localLocation.lat}, ${localLocation.lng}`);
    return localLocation;
  }

  // ── NIVEL 2: última orden del cliente (cross-device) ─────────────
  // Solo disponible si el cliente está logueado y su última orden
  // tenía delivery. Inyectado por Liquid al renderizar el carrito.
  // Lo guardamos en localStorage SIN sessionActive para que sirva
  // como referencia pero no compita con una sesión real del NIVEL 1.
  if (window.__savedDeliveryLocation) {
    console.log('📍 NIVEL 2 — Cross-device: ubicación desde última orden del cliente:', window.__savedDeliveryLocation);

    // Persistir como referencia sin marcar sesión activa.
    // sessionActive se activará recién cuando el cliente confirme en el mapa.
    const comoReferencia = { ...window.__savedDeliveryLocation, sessionActive: false };
    localStorage.setItem(this.storageKey, JSON.stringify(comoReferencia));

    return window.__savedDeliveryLocation;
  }

  // ── NIVEL 3: localStorage sin sesión activa (fallback) ───────────
  // No hay sesión activa ni datos del servidor.
  // Usamos lo que haya como orientación geográfica.
  if (localLocation?.valid) {
    console.log('📍 NIVEL 3 — Fallback: ubicación de referencia en localStorage:', localLocation);
    return localLocation;
  }

  // ── Sin datos en ningún nivel ─────────────────────────────────────
  console.log('ℹ️ Sin ubicación guardada — el mapa abrirá en coordenadas por defecto (Asunción)');
  return null;
}

  /**
   * Guarda la ubicación en localStorage marcándola como sesión activa.
   *
   * `sessionActive: true` es la señal que usa loadLocation() NIVEL 1
   * para saber que el cliente confirmó esta ubicación en la sesión actual.
   * Este flag se limpia desde main-cart-items.liquid cuando:
   *   - El carrito queda en 0 items (compra completada o vaciado manual)
   *   - El cliente hace logout
   */
  saveLocation(lat, lng, address) {
    const location = {
      lat,
      lng,
      address,
      valid: true,
      sessionActive: true  // Indica que fue confirmada en esta sesión
    };
    localStorage.setItem(this.storageKey, JSON.stringify(location));
    console.log('💾 Ubicación guardada como sesión activa:', location);
    return location;
  }

  /**
   * Verifica si un punto está dentro del área de cobertura
   */
  isInsideCoverageArea(lat, lng) {
    if (!this.coverageArea) return false;
    
    const point = new google.maps.LatLng(lat, lng);
    return google.maps.geometry.poly.containsLocation(point, this.coverageArea);
  }

  /**
   * Inicializa el mapa de Google Maps
   */
  async initMap() {
    const mapContainer = document.getElementById(this.mapElementId);
    if (!mapContainer) {
      console.error(`Contenedor de mapa #${this.mapElementId} no encontrado`);
      return;
    }

    // Verificar que Google Maps esté cargado
    if (typeof google === 'undefined' || !google.maps) {
      console.error('Google Maps no está cargado');
      return;
    }

    // Coordenadas iniciales
    const initialLat = this.currentLocation?.lat || this.defaultLat;
    const initialLng = this.currentLocation?.lng || this.defaultLng;

    // Crear mapa
    this.map = new google.maps.Map(mapContainer, {
      center: { lat: initialLat, lng: initialLng },
      zoom: 13,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true
    });

    // Crear geocoder
    this.geocoder = new google.maps.Geocoder();

    // 🔴 CREAR POLÍGONO DE ÁREA DE COBERTURA
    const polygonCoords = this.coverageCoordinates.map(coord => ({
      lat: coord[0],
      lng: coord[1]
    }));

    this.coverageArea = new google.maps.Polygon({
      paths: polygonCoords,
      strokeColor: '#ef1e27',
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#ef1e27',
      fillOpacity: 0.15,
      clickable: false,
      editable: false
    });
/*
    // rojo
strokeColor: '#ef1e27',
fillColor: '#ef1e27',
    // Verde
strokeColor: '#4CAF50',
fillColor: '#4CAF50',

// Azul
strokeColor: '#2196F3',
fillColor: '#2196F3',

// Naranja
strokeColor: '#FF9800',
fillColor: '#FF9800',

// Morado
strokeColor: '#9C27B0',
fillColor: '#9C27B0',

// Azul oscuro (como el logo de tu tienda si lo tienes)
strokeColor: '#1565C0',
fillColor: '#1565C0',
*/
    this.coverageArea.setMap(this.map);

    // Si hay ubicación guardada en storage, centrar y hacer zoom sobre ella.
    // Si no, mostrar toda el área de cobertura con fitBounds.
    if (this.currentLocation?.valid === true) {
      this.map.setCenter({ lat: initialLat, lng: initialLng });
      this.map.setZoom(16);
    } else {
      const bounds = new google.maps.LatLngBounds();
      polygonCoords.forEach(coord => bounds.extend(coord));
      this.map.fitBounds(bounds);
    }

    // Crear marcador arrastrable
    this.marker = new google.maps.Marker({
      position: { lat: initialLat, lng: initialLng },
      map: this.map,
      draggable: true,
      animation: google.maps.Animation.DROP,
      title: 'Arrastra para ajustar tu ubicación'
    });

    // Si la ubicación viene del storage y ya fue confirmada como válida,
    // habilitamos el botón ANTES de validar (la validación puede fallar si
    // el polígono aún no está completamente inicializado)
    if (this.currentLocation?.valid === true) {
      const confirmBtn = document.getElementById('confirm-delivery-location-btn');
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.classList.remove('disabled');
        console.log('✅ Botón habilitado desde ubicación guardada en storage');
      }
    }

    // Validar ubicación inicial
    this.validateAndUpdateLocation(initialLat, initialLng);

    // Event listener para cuando se arrastra el marcador
    this.marker.addListener('dragend', (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.validateAndUpdateLocation(lat, lng);
    });

    // Event listener para click en el mapa
    this.map.addListener('click', (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.marker.setPosition({ lat, lng });
      this.validateAndUpdateLocation(lat, lng);
    });

    console.log('✅ Mapa de delivery inicializado con área de cobertura');
  }

  /**
   * Valida y actualiza la ubicación (verifica que esté en el área de cobertura)
   */
  validateAndUpdateLocation(lat, lng) {
    const isInside = this.isInsideCoverageArea(lat, lng);
    
    if (isInside) {
      // ✅ Dentro del área de cobertura
      this.updateLocation(lat, lng);
      this.hideWarning();
      
      const confirmBtn = document.getElementById('confirm-delivery-location-btn');
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.classList.remove('disabled');
      }
    } else {
      // ❌ Fuera del área — pero si el polígono aún no está listo
      // isInsideCoverageArea devuelve false aunque la ubicación sea válida.
      // Solo deshabilitar si el polígono existe Y realmente está afuera,
      // o si no hay ubicación válida guardada en storage.
      const polygonReady = !!this.coverageArea;
      const hasValidStoredLocation = this.currentLocation?.valid === true;

      if (polygonReady || !hasValidStoredLocation) {
        this.showWarning();
        this.currentLocation = { lat, lng, address: null, valid: false };
        
        const confirmBtn = document.getElementById('confirm-delivery-location-btn');
        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.classList.add('disabled');
        }
        
        if (this.addressInfoElement) {
          this.addressInfoElement.textContent = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
        }
      }
    }
  }

  /**
   * Actualiza la ubicación seleccionada
   */
  updateLocation(lat, lng) {
    this.reverseGeocode(lat, lng);
    this.currentLocation = { lat, lng, valid: true };
    
    // Callback personalizado
    if (this.onLocationSelect && typeof this.onLocationSelect === 'function') {
      this.onLocationSelect({ lat, lng });
    }
  }

  /**
   * Muestra el mensaje de advertencia (fuera del área)
   */
  showWarning() {
    if (this.warningElement) {
      this.warningElement.style.display = 'block';
    }
  }

  /**
   * Oculta el mensaje de advertencia
   */
  hideWarning() {
    if (this.warningElement) {
      this.warningElement.style.display = 'none';
    }
  }

  /**
   * Obtiene la dirección a partir de coordenadas (geocoding inverso)
   */
  reverseGeocode(lat, lng) {
    if (!this.geocoder) return;

    const latLng = { lat, lng };
    
    this.geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const address = results[0].formatted_address;
        this.updateAddressDisplay(address);
        if (this.currentLocation) {
          this.currentLocation.address = address;
        }
      } else {
        console.error('Geocoding error:', status);
        this.updateAddressDisplay(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
      }
    });
  }

  /**
   * Actualiza el display de la dirección
   */
  updateAddressDisplay(address) {
    if (this.addressInfoElement) {
      this.addressInfoElement.textContent = address;
    }
  }

  /**
   * Intenta obtener la ubicación actual del usuario
   */
  getCurrentPosition() {
    if (!navigator.geolocation) {
      console.warn('Geolocalización no soportada por el navegador');
      return;
    }

    const loadingElement = document.getElementById('geolocation-loading');
    if (loadingElement) loadingElement.style.display = 'block';

    navigator.geolocation.getCurrentPosition(
      // Success
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        console.log('📍 Ubicación del usuario obtenida:', { lat, lng });
        
        // Actualizar mapa y marcador
        this.marker.setPosition({ lat, lng });
        this.map.setCenter({ lat, lng });
        this.map.setZoom(16);
        
        this.validateAndUpdateLocation(lat, lng);
        
        if (loadingElement) loadingElement.style.display = 'none';
      },
      // Error
      (error) => {
        console.error('Error al obtener ubicación:', error);
        if (loadingElement) {
          loadingElement.style.display = 'none';
        }
        
        let message = 'No se pudo obtener tu ubicación.';
        if (error.code === 1) {
          message = 'Permiso de ubicación denegado. Por favor, selecciona tu ubicación en el mapa.';
        }
        alert(message);
      },
      // Options
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }

  /**
   * Renderiza la interfaz completa
   */
  render() {
    const container = document.getElementById(this.containerId);
    if (!container) {
      console.error(`Container #${this.containerId} no encontrado`);
      return;
    }

    container.innerHTML = `
      <div class="delivery-geo-actions">
        <button type="button" class="delivery-geo-btn delivery-geo-btn--current" id="get-current-location">
          📍 Usar mi ubicación actual
        </button>
      </div>

      <div id="geolocation-loading" class="delivery-geo-loading" style="display: none;">
        <div class="spinner"></div>
        <p>Obteniendo tu ubicación...</p>
      </div>

      <div id="coverage-warning" class="delivery-geo-warning" style="display: none;">
        <div class="delivery-geo-warning-icon">⚠️</div>
        <div class="delivery-geo-warning-content">
          <strong>Ubicación fuera del área de cobertura</strong>
          <p>Por favor, selecciona una ubicación dentro del área marcada en rojo en el mapa.</p>
        </div>
      </div>

      <div class="delivery-geo-map-container">
        <div id="${this.mapElementId}" class="delivery-geo-map"></div>
      </div>

      <div class="delivery-geo-info geo-hidden">
        <div class="delivery-geo-info-header">
          <strong>📌 Dirección seleccionada:</strong>
        </div>
        <div id="selected-address" class="delivery-geo-address">
          ${this.currentLocation?.address || 'Selecciona una ubicación en el mapa...'}
        </div>
      </div>

      <div class="delivery-geo-footer">
        <p class="delivery-geo-hint">
          💡 <strong>Tip:</strong> Arrastra el marcador o haz clic en el mapa para ajustar tu ubicación
        </p>
      </div>
    `;

    // Guardar referencias
    this.addressInfoElement = document.getElementById('selected-address');
    this.warningElement = document.getElementById('coverage-warning');
    
    // Botón de ubicación actual
    const currentLocationBtn = document.getElementById('get-current-location');
    if (currentLocationBtn) {
      currentLocationBtn.addEventListener('click', () => {
        this.getCurrentPosition();
      });
    }

    // Inicializar mapa después de renderizar
    setTimeout(() => {
      this.initMap();
    }, 100);

    console.log('✅ Interfaz de geolocalización renderizada');
  }

  /**
   * Obtiene la ubicación seleccionada actual
   */
  getSelectedLocation() {
    return this.currentLocation;
  }

  /**
   * Genera el enlace de Google Maps
   */
  getGoogleMapsLink() {
    if (!this.currentLocation) return '';
    const { lat, lng } = this.currentLocation;
   // return `https://www.google.com/maps?q=${lat},${lng}`;
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  /**
   * Actualiza el atributo en el carrito de Shopify
   */
  async updateCartAttribute() {
    if (!this.currentLocation) {
      console.error('No hay ubicación seleccionada');
      return false;
    }

    if (!this.currentLocation.valid) {
      alert('La ubicación seleccionada está fuera del área de cobertura.');
      return false;
    }

    try {
      const mapsLink = this.getGoogleMapsLink();
      const formData = new FormData();
      
      // Guardar el link de Google Maps en note
      formData.append('note', mapsLink);
      //formData.append('attributes[url_maps]', mapsLink);  
      // También guardar las coordenadas como atributos
      formData.append('attributes[delivery_latitude]', this.currentLocation.lat);
      formData.append('attributes[delivery_longitude]', this.currentLocation.lng);
      if (this.currentLocation.address) {
        formData.append('attributes[delivery_address]', this.currentLocation.address);
      }
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error al actualizar el carrito');
      }

      // Guardar en localStorage
      this.saveLocation(
        this.currentLocation.lat, 
        this.currentLocation.lng, 
        this.currentLocation.address
      );

      console.log('✅ Ubicación de delivery guardada en carrito');
      console.log('🔗 Google Maps Link:', mapsLink);
      
      return await response.json();
    } catch (error) {
      console.error('❌ Error al actualizar ubicación:', error);
      throw error;
    }
  }

  /**
   * Limpia la ubicación guardada
   */
  clearStorage() {
    localStorage.removeItem(this.storageKey);
    console.log('🗑️ Ubicación de delivery limpiada');
  }

  /**
   * Valida que hay una ubicación seleccionada Y que está en el área
   */
  isValid() {
    return this.currentLocation && 
           typeof this.currentLocation.lat === 'number' && 
           typeof this.currentLocation.lng === 'number' &&
           this.currentLocation.valid === true;
  }
}

// Exportar para uso global
window.DeliveryGeolocation = DeliveryGeolocation;